import { prepareScheduledDonationPayload } from './scheduleConfigUtils';
import { getMonthlyBumpMetadata } from './monthlyBumpUtils';

const AMOUNT_ARRAY_KEYS = [
  'oneTimeAmounts',
  'dailyAmounts',
  'weeklyAmounts',
  'monthlyAmounts',
  'annuallyAmounts',
];

const DEFAULT_AMOUNT_KEYS = [
  'defaultOneTimeAmount',
  'defaultDailyAmount',
  'defaultWeeklyAmount',
  'defaultMonthlyAmount',
  'defaultAnnuallyAmount',
];

export const applyScheduledDonationPayloadToData = ({
  data,
  frequency,
  startDate,
  endDate,
  timeValue,
}) => {
  if (frequency === 'ONE_TIME') {
    return data;
  }

  const scheduledPayload = prepareScheduledDonationPayload(startDate, endDate, timeValue);
  data.localStartDateTime = scheduledPayload.localStartDateTime;
  data.localEndDateTime = scheduledPayload.localEndDateTime;
  data.utcStartDateTime = scheduledPayload.utcStartDateTime;
  data.utcEndDateTime = scheduledPayload.utcEndDateTime;
  data.userTimezone = scheduledPayload.userTimezone;
  data.timeValue = scheduledPayload.timeValue;

  return data;
};

/**
 * Coerce one form-config amounts value into an array of amount strings.
 *
 * The canonical shape is an array of numbers, but a config can reach the widget
 * malformed, and every variant used to hit `.map()` directly and throw a
 * TypeError — which white-screens the entire donation form:
 *
 *   - MISSING or null: /elements-config omits these attributes for some forms,
 *     and a large number of form records store them as DynamoDB NULL.
 *   - AN OBJECT: a QUICK_DONATE -> EMBED_FORM migration wrote
 *     {amount1..amount4, defaultAmount} into 5 prod records in 2025-12. That
 *     object is dashboard UI-local state, never the persisted schema.
 *
 * A donor who sees no preset buttons can still type a custom amount; a donor who
 * sees a blank iframe is a lost donation. So degrade, never throw.
 *
 * An empty result is NOT substituted with defaults: "no preset buttons,
 * custom amount only" is a legitimate configuration that must be preserved.
 */
export const coerceAmountArray = (value) => {
  const candidates = (() => {
    if (Array.isArray(value)) {
      return value;
    }
    if (value && typeof value === 'object') {
      // Order by the trailing integer so amount10 follows amount9 rather than
      // amount1. `defaultAmount` is excluded — it is not an amount button, and
      // the real default lives in the sibling default<Frequency>Amount field.
      return Object.keys(value)
        .filter((key) => /^amount\d+$/.test(key))
        .sort(
          (a, b) => Number(a.replace('amount', '')) - Number(b.replace('amount', '')),
        )
        .map((key) => value[key]);
    }
    return [];
  })();

  // Drop blanks rather than stringifying them, mirroring what the dashboard does
  // when it flattens its own amount object on write.
  return candidates
    .filter((amount) => amount !== null && amount !== undefined && amount !== '')
    .map((amount) => amount.toString());
};

/**
 * Normalize every per-frequency amounts array on a fetched form config to
 * arrays of strings, tolerating missing/null/object values. See coerceAmountArray.
 */
export const normalizeAmountArraysToStrings = (data) => {
  AMOUNT_ARRAY_KEYS.forEach((key) => {
    data[key] = coerceAmountArray(data[key]);
  });

  return data;
};

export const normalizeRecurringAmountConfig = (data) => {
  AMOUNT_ARRAY_KEYS.forEach((key) => {
    if (Array.isArray(data[key])) {
      data[key] = data[key].map((amount) => parseFloat(amount));
    }
  });

  DEFAULT_AMOUNT_KEYS.forEach((key) => {
    data[key] = parseFloat(data[key]) || 0;
  });

  return data;
};

export const buildPreGeneratedCartItemKey = ({
  frequency,
  fund,
  amount,
  formName,
  localStartDateTime,
  customInputsValues = [],
  extraKeyParts = [],
}) => {
  const baseKey = [frequency, fund, amount, formName, ...extraKeyParts]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join('_');

  let cartItemKey = baseKey;

  if (frequency !== 'ONE_TIME' && localStartDateTime) {
    const dateStr = localStartDateTime.split('T')[0].replace(/-/g, '');
    cartItemKey += `_${dateStr}`;
  }

  if (Array.isArray(customInputsValues) && customInputsValues.length > 0) {
    const customInputsSuffix = customInputsValues
      .slice()
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      .map((input) => {
        const answer = input.value || (input.answers && input.answers[0]) || '';
        return `${input.title || ''}:${answer}`;
      })
      .join('_')
      .replace(/[^a-zA-Z0-9_]/g, '_');

    cartItemKey += `_${customInputsSuffix}`;
  }

  return cartItemKey;
};

export const attachCartSubmissionMetadata = ({
  data,
  formSpecificKeyParts = [],
}) => {
  const preGeneratedCartItemKey = buildPreGeneratedCartItemKey({
    frequency: data.frequency,
    fund: data.fund,
    amount: data.amount,
    formName: data.formName,
    localStartDateTime: data.localStartDateTime,
    customInputsValues: data.customInputsValues,
    extraKeyParts: formSpecificKeyParts,
  });

  const fundItemId = `fund_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

  data.preGeneratedCartItemKey = preGeneratedCartItemKey;
  data.cartItemKey = preGeneratedCartItemKey;
  data.fundItemId = fundItemId;

  if (Array.isArray(data.customInputsValues) && data.customInputsValues.length > 0) {
    data.customInputsValues = data.customInputsValues.map((input) => ({
      ...input,
      fundItemId,
      inputType: 'fund',
      fundId: data.formID,
    }));
  }

  return {
    data,
    preGeneratedCartItemKey,
    fundItemId,
  };
};

/**
 * Build a single cart-item object from a form's submission data.
 *
 * WHY THIS EXISTS
 * ---------------
 * Historically, only ProductForm and the AddToCart flow emitted rich cart items
 * in the DONATE postMessage. StandardForm and CrowdfundingForm direct-donate
 * submissions sent `data.cartItems = undefined` (explicitly deleted), leaving
 * the checkout's `state.cart` empty. That caused the checkout to POST
 * `funds: []` to transactions-backend, which silently fell back to
 * `convert_fund_to_funds` (card.py:1962). That fallback synthesizes a minimal
 * 10-field fund WITHOUT `fundItemId`, which in turn breaks downstream logic
 * that matches custom inputs to cart items via `fundItemId` (see
 * donation-checkout/DonorInfo.jsx:819-826).
 *
 * This builder centralizes the cart-item shape so all three forms (Standard,
 * Crowdfunding, Product) emit the same structure, eliminating the fallback
 * path for donor-facing submissions and preserving frontend-generated
 * metadata (fundItemId, cartItemKey, schedule dates, custom-input linkage)
 * through to DynamoDB.
 *
 * CALLER CONTRACT
 * ---------------
 * - `attachCartSubmissionMetadata` MUST have run on `data` first: this sets
 *   `data.cartItemKey` and `data.fundItemId`, which are required here.
 * - For recurring frequencies, `applyScheduledDonationPayloadToData` MUST
 *   have run on `data` first to populate the local/UTC datetime fields.
 * - Mutates `data.customInputsValues[i].cartItemKey` as a side effect so
 *   downstream custom-input-to-fund matching works (same side effect that
 *   used to live inline in ProductForm.jsx:1322-1324).
 *
 * SHAPE NOTE
 * ----------
 * Intentionally does NOT emit a backend-style `id` UUID field. The backend's
 * convert_fund_to_funds fallback sets `id = uuid.uuid4()` when funds=[]; our
 * path bypasses that fallback and uses frontend-generated `fundItemId` for
 * stable linkage. See the audit log discussion in the PR for downstream
 * consumers that read `fund.id` — all have graceful fallbacks to
 * `cartItemKey`.
 */
export const buildCartItemFromSubmission = ({
  data,
  amount,
  quantity = 1,
}) => {
  const monthlyBumpMetadata = getMonthlyBumpMetadata(data);

  const cartItem = {
    cartItemKey: data.cartItemKey,
    fundItemId: data.fundItemId,
    salesforceCampaignID: data.salesforceCampaignID,
    frequency: data.frequency,
    fundName: data.fund,
    amount,
    quantity,
    fundraiser: data.formName,
    formID: data.formID,
    crowdfundingOption: data.crowdfundingOption,
    ...monthlyBumpMetadata,
    // Scheduled-donation datetime fields: only meaningful for recurring.
    // ONE_TIME donations must not carry these, or downstream schedule-matching
    // treats the donation as a scheduled/future charge.
    ...(data.frequency !== 'ONE_TIME' && {
      localStartDateTime: data.localStartDateTime,
      localEndDateTime: data.localEndDateTime,
      utcStartDateTime: data.utcStartDateTime,
      utcEndDateTime: data.utcEndDateTime,
      timeValue: data.timeValue,
    }),
  };

  // Product fields: only for ProductForm, which populates data.products.
  // Note: this overrides `quantity` with products[0].quantity, matching
  // legacy ProductForm behavior. StandardForm/CrowdfundingForm won't set
  // data.products, so this block is a no-op for them.
  if (Array.isArray(data.products) && data.products.length > 0) {
    cartItem.productID = data.products[0].productID;
    cartItem.productGroupName = data.products[0].productGroupName;
    cartItem.productGroupDescription = data.products[0].productGroupDescription;
    cartItem.productName = data.products[0].productName;
    cartItem.productPrice = data.products[0].productPrice;
    cartItem.quantity = data.products[0].quantity;
  }

  // Spread mappedData (from FieldMapper) onto the cart item. Keys are
  // nonprofit-specific integration fields (e.g. NETSUITE_Program, sku).
  if (data.mappedData) {
    Object.entries(data.mappedData).forEach(([key, value]) => {
      cartItem[key] = value;
    });
  }

  // Attach custom inputs for cart UI display (enables AddToCartSheet and
  // DonationSummary to show per-item answers like "Region: Turkey"). Without
  // this, items with different custom-input answers render identically.
  if (Array.isArray(data.customInputsValues) && data.customInputsValues.length > 0) {
    cartItem.customInputs = data.customInputsValues;
  }

  // Stamp cartItemKey onto each customInput so the backend (DonorInfo.jsx
  // custom-input-to-fund matcher, notifications export, etc.) can link
  // per-fund answers to their cart item. Preserves the side effect that
  // previously lived inline in ProductForm.jsx:1322-1324.
  data.customInputsValues?.forEach((customInput) => {
    customInput.cartItemKey = data.cartItemKey;
  });

  return cartItem;
};
